import React, { useEffect, useLayoutEffect, useMemo, useRef, useState, useCallback } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import './Favoritos.css';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../../firebase/firebase';

// Sanity
import client from '../../sanity/SanityClient';
import imageUrlBuilder from '@sanity/image-url';

const builder = imageUrlBuilder(client);
const urlFor = (source) => builder.image(source);

const ORIENT = { V: 'V', H: 'H' };
const PAGE_SIZE = 10;

// Keys de estado persistente
const GRID_STATE_KEY = (pathname, search) => `favGridState:${pathname}${search || ''}`; // y, page, sortKey, lastId, query
const ORDER_STATE_KEY = (pathname) => `favOrder:${pathname}`; // orden base de items (ids)

// ===== localStorage helpers =====
const readRawFavs = () => {
  try { return JSON.parse(localStorage.getItem('favoritos')) || []; }
  catch { return []; }
};
const writeRawFavs = (arr) => localStorage.setItem('favoritos', JSON.stringify(arr));
const isUrlLike = (v) => typeof v === 'string' && /^(https?:)?\/\//i.test(v);

// ===== imagen/orientación helpers =====
function fromAsset(imgField) {
  const asset = imgField?.asset;
  if (!asset) return null;
  const url = asset.url || urlFor(imgField).width(1200).auto('format').url();
  const dims = asset.metadata?.dimensions;
  const w = dims?.width ?? null;
  const h = dims?.height ?? null;
  let orientation = null;
  if (w && h) orientation = h > w ? ORIENT.V : ORIENT.H;
  return { url, w, h, orientation, ratio: w && h ? w / h : null };
}
async function ensureOrientation(items) {
  const unresolved = items.filter((it) => !it.orientation || !it.ratio);
  await Promise.all(
    unresolved.map((it) => new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const nw = img.naturalWidth || 1, nh = img.naturalHeight || 1;
        if (!it.orientation) it.orientation = nh > nw ? ORIENT.V : ORIENT.H;
        if (!it.ratio) it.ratio = nw / nh;
        resolve();
      };
      img.onerror = () => resolve();
      img.src = it.imageUrl;
    }))
  );
  return items;
}
function shuffle(a0) {
  const a = [...a0];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}
function buildRows(items) {
  const V = items.filter(i => i.orientation === ORIENT.V);
  const H = items.filter(i => i.orientation === ORIENT.H);
  const rows = [];
  const patterns = ['VHH','HVH','HHV'];
  let p = 0;
  const v = [...V], h = [...H];
  const take = (arr) => (arr.length ? arr.shift() : null);

  while (v.length || h.length) {
    const pat = patterns[p % patterns.length];
    if (pat === 'VHH' && v.length >= 1 && h.length >= 2) rows.push([take(v), take(h), take(h)].filter(Boolean));
    else if (pat === 'HVH' && h.length >= 1 && v.length >= 1 && h.length >= 1) rows.push([take(h), take(v), take(h)].filter(Boolean));
    else if (pat === 'HHV' && h.length >= 2 && v.length >= 1) rows.push([take(h), take(h), take(v)].filter(Boolean));
    else if (h.length >= 3) rows.push([take(h), take(h), take(h)].filter(Boolean));
    else if (v.length >= 1 && h.length >= 1) rows.push([take(v), take(h), take(h) || take(v) || null].filter(Boolean));
    else {
      const last = [take(v) || take(h), take(v) || take(h), take(v) || take(h)].filter(Boolean);
      if (last.length) rows.push(last);
      break;
    }
    p++;
  }
  return rows.filter(r => r.length > 0);
}
function computeImageBox(cardEl, ratio) {
  const fallback = { w: 0, h: 0 };
  if (!cardEl) return fallback;
  const Wc = cardEl.clientWidth || cardEl.getBoundingClientRect().width || 0;
  const Hc = cardEl.clientHeight || cardEl.getBoundingClientRect().height || 0;
  if (!Wc || !Hc) return fallback;
  const r = Number(ratio) > 0 ? Number(ratio) : 1;
  const cRatio = Wc / Hc;
  if (cRatio > r) {
    const imgW = Hc * r;
    return { w: Math.round(imgW), h: Math.round(Hc) };
  } else {
    const imgH = Wc / r;
    return { w: Math.round(Wc), h: Math.round(imgH) };
  }
}

const Favoritos = () => {
  const history = useHistory();
  const location = useLocation();

  // ===== Estado inicial restaurable desde URL + sessionStorage =====
  const initialFromStorage = (() => {
    const params = new URLSearchParams(location.search);
    const urlSort = params.get('sort');
    const urlPage = parseInt(params.get('page') || '1', 10);
    const q = params.get('q') || '';

    const key = GRID_STATE_KEY(location.pathname, location.search);
    let saved = null; try { saved = JSON.parse(sessionStorage.getItem(key) || 'null'); } catch {}

    const sort =
      (saved?.sortKey && ['random','recent','az','za'].includes(saved.sortKey)) ? saved.sortKey :
      (urlSort && ['random','recent','az','za'].includes(urlSort)) ? urlSort :
      'random';

    const page =
      (typeof saved?.page === 'number' && saved.page >= 1) ? saved.page :
      (!Number.isNaN(urlPage) && urlPage >= 1) ? urlPage :
      1;

    const query = typeof saved?.query === 'string' ? saved.query : q;

    return {
      sortKey: sort,
      page,
      query,
      restoreY: typeof saved?.y === 'number' ? saved.y : null,
      lastId: saved?.lastId || null,
    };
  })();

  // auth
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem('user')) || null; } catch { return null; }
  });

  // dataset
  const [rawItems, setRawItems] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  // UI state (URL-driven + restaurable)
  const [sortKey, setSortKey] = useState(() => initialFromStorage.sortKey); // 'random' | 'recent' | 'az' | 'za'
  const [query, setQuery] = useState(() => initialFromStorage.query || '');
  const [page, setPage] = useState(() => initialFromStorage.page);

  // flags para no resetear en la restauración inicial
  const restoringSortRef = useRef(true);
  const restoringQueryRef = useRef(true);

  // restauración de scroll
  const desiredYRef = useRef(initialFromStorage.restoreY);
  const pendingIdRef = useRef(initialFromStorage.lastId);
  const restoreTriesRef = useRef(0);

  // orden base estable
  const savedOrderRef = useRef(null); // array de ids en orden
  const rawIndexRef = useRef({});     // id -> índice base

  // layout positioning refs
  const cardsRef = useRef({});   // id -> HTMLElement
  const ratioRef = useRef({});   // id -> ratio
  const [imgBox, setImgBox] = useState({}); // id -> {w,h}
  const roRef = useRef(null);

  // cargar orden persistente al entrar en la ruta
  useEffect(() => {
    const ok = ORDER_STATE_KEY(location.pathname);
    try { savedOrderRef.current = JSON.parse(sessionStorage.getItem(ok) || 'null'); } catch { savedOrderRef.current = null; }
  }, [location.pathname]);

  useLayoutEffect(() => {
    roRef.current = new ResizeObserver((entries) => {
      const patch = {};
      for (const entry of entries) {
        const el = entry.target;
        const id = el.getAttribute('data-id');
        if (!id) continue;
        const ratio = ratioRef.current[id] || 1;
        patch[id] = computeImageBox(el, ratio);
      }
      if (Object.keys(patch).length) setImgBox((prev) => ({ ...prev, ...patch }));
    });
    return () => roRef.current?.disconnect();
  }, []);
  useEffect(() => {
    const ro = roRef.current;
    Object.values(cardsRef.current).forEach((el) => el && ro?.unobserve(el));
    Object.entries(cardsRef.current).forEach(([id, el]) => el && ro?.observe(el));
    const patch = {};
    Object.entries(cardsRef.current).forEach(([id, el]) => {
      const ratio = ratioRef.current[id] || 1;
      patch[id] = computeImageBox(el, ratio);
    });
    if (Object.keys(patch).length) setImgBox((prev) => ({ ...prev, ...patch }));
    return () => { Object.values(cardsRef.current).forEach((el) => el && ro?.unobserve(el)); };
  }, [rows]);

  // Sincronizar URL <-> estado (incluye query)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    params.set('sort', sortKey);
    params.set('page', String(page));
    if (query) params.set('q', query); else params.delete('q');
    history.replace({ pathname: location.pathname, search: params.toString() });
  }, [sortKey, page, query, history, location.pathname, location.search]);

  // Evitar que el navegador intente restaurar solo y cause flicker
  useEffect(() => {
    let prev = null;
    try {
      if ('scrollRestoration' in window.history) {
        prev = window.history.scrollRestoration;
        window.history.scrollRestoration = 'manual';
      }
    } catch {}
    return () => {
      try {
        if ('scrollRestoration' in window.history) {
          window.history.scrollRestoration = prev || 'auto';
        }
      } catch {}
    };
  }, []);

  // ==== Normalizar item key (sirve para URLs y objetos) ====
  const getItemKey = (it) => it._id || it.imageUrl;

  // Cargar favoritos
  const loadFavoritos = useCallback(async () => {
    setLoading(true);
    try {
      const raw = readRawFavs();
      const asArray = Array.isArray(raw) ? raw : [];

      const idSet = new Set();
      const urlItems = [];
      const objItems = [];

      asArray.forEach((entry) => {
        if (entry && typeof entry === 'object' && entry.imageUrl) {
          objItems.push({
            _id: entry._id || entry.imageUrl,
            titulo: entry.titulo || '',
            imageUrl: entry.imageUrl,
            createdAt: entry._createdAt || null,
          });
        } else if (typeof entry === 'string') {
          if (isUrlLike(entry)) {
            urlItems.push({ _id: entry, titulo: '', imageUrl: entry, createdAt: null });
          } else {
            idSet.add(entry);
          }
        }
      });

      // Buscar en Sanity
      let sanityItems = [];
      if (idSet.size) {
        const ids = Array.from(idSet);
        const q = `
          *[
            _type == "foto" &&
            !(_id in path("drafts.**")) &&
            _id in $ids
          ]{
            _id, _createdAt, titulo,
            previewImage{asset->{url, metadata{dimensions{width,height,aspectRatio}}}},
            detalleImage{asset->{url, metadata{dimensions{width,height,aspectRatio}}}}
          }
        `;
        const docs = await client.fetch(q, { ids });
        sanityItems = (docs || [])
          .map((d) => {
            const primary = fromAsset(d.previewImage) || fromAsset(d.detalleImage);
            return {
              _id: d._id,
              titulo: d.titulo || '',
              imageUrl: primary?.url || null,
              createdAt: d._createdAt || null,
              orientation: primary?.orientation || null,
              ratio: primary?.ratio || null,
            };
          })
          .filter(x => !!x.imageUrl);
      }

      // Merge + completar orientación
      let items = await ensureOrientation([...sanityItems, ...urlItems, ...objItems]);

      // ===== ORDEN ALEATORIO ESTABLE =====
      const orderKey = ORDER_STATE_KEY(location.pathname);
      const savedOrder = Array.isArray(savedOrderRef.current) ? savedOrderRef.current : null;

      if (savedOrder && savedOrder.length) {
        // Reordenar según orden guardado; lo nuevo va al final
        const pos = new Map(savedOrder.map((id, i) => [id, i]));
        items.sort((a, b) => {
          const ka = getItemKey(a);
          const kb = getItemKey(b);
          const pa = pos.has(ka) ? pos.get(ka) : Number.MAX_SAFE_INTEGER;
          const pb = pos.has(kb) ? pos.get(kb) : Number.MAX_SAFE_INTEGER;
          return pa - pb;
        });
      } else {
        // Primera vez: fijar orden aleatorio y guardarlo
        items = shuffle(items);
        const order = items.map(it => getItemKey(it));
        try { sessionStorage.setItem(orderKey, JSON.stringify(order)); } catch {}
        savedOrderRef.current = order;
      }

      // Índice estable (para ordenar por 'random' sin rebarajar)
      const idx = {};
      items.forEach((it, i) => { idx[getItemKey(it)] = i; });
      rawIndexRef.current = idx;

      setRawItems(items);
    } finally {
      setLoading(false);
    }
  }, [location.pathname]);

  // Auth + sync
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (fbUser) => {
      if (fbUser) {
        const cached = { uid: fbUser.uid, email: fbUser.email, name: fbUser.displayName || '' };
        localStorage.setItem('user', JSON.stringify(cached));
        setUser(cached);
        loadFavoritos();
      } else {
        localStorage.removeItem('user');
        setUser(null);
        setRawItems([]);
      }
    });
    return () => unsubscribe();
  }, [loadFavoritos]);

  // sync con cambios en otra pestaña
  useEffect(() => {
    const onStorage = (e) => { if (e.key === 'favoritos' && user) loadFavoritos(); };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [user, loadFavoritos]);

  // ====== Filtro por búsqueda ======
  const filteredItems = useMemo(() => {
    const q = (query || '').trim().toLowerCase();
    if (!q) return rawItems;
    return rawItems.filter(it => (it.titulo || '').toLowerCase().includes(q));
  }, [rawItems, query]);

  // ====== Orden ======
  const sortedItems = useMemo(() => {
    let arr = [...filteredItems];
    switch (sortKey) {
      case 'recent':
        arr.sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || '')); break;
      case 'az':
        arr.sort((a, b) => (a.titulo || '').localeCompare(b.titulo || '', undefined, { sensitivity: 'base' })); break;
      case 'za':
        arr.sort((a, b) => (b.titulo || '').localeCompare(a.titulo || '', undefined, { sensitivity: 'base' })); break;
      case 'random':
      default:
        // Orden estable según índice base (NO rebarajar)
        arr.sort((a, b) => {
          const ka = getItemKey(a), kb = getItemKey(b);
          return (rawIndexRef.current[ka] ?? 0) - (rawIndexRef.current[kb] ?? 0);
        });
        break;
    }
    return arr;
  }, [filteredItems, sortKey]);

  // ====== Layout APPEND-ONLY (como sub/categorías) ======
  const allRows = useMemo(() => {
    const built = buildRows(sortedItems).map(r => r.filter(Boolean)).filter(r => r.length > 0);
    return built;
  }, [sortedItems]);

  const visibleCountTarget = useMemo(() => {
    return Math.min(page * PAGE_SIZE, sortedItems.length);
  }, [sortedItems.length, page]);

  const visibleRows = useMemo(() => {
    let acc = 0;
    const out = [];
    for (const row of allRows) {
      if (acc >= visibleCountTarget) break;
      out.push(row);
      acc += row.length;
    }
    return out;
  }, [allRows, visibleCountTarget]);

  useEffect(() => { setRows(visibleRows); }, [visibleRows]);

  // reset page si el cambio de sort es manual (no en restauración)
  useEffect(() => {
    if (restoringSortRef.current) { restoringSortRef.current = false; return; }
    setPage(1);
  }, [sortKey]);

  // búsqueda => page 1 solo si viene del usuario (no en restauración)
  useEffect(() => { if (restoringQueryRef.current) return; setPage(1); }, [query]);
  useEffect(() => { const t = setTimeout(() => { restoringQueryRef.current = false; }, 0); return () => clearTimeout(t); }, []);

  // ===== quitar de favoritos =====
  const removeFromFavoritos = useCallback((keyToRemove) => {
    const raw = readRawFavs();
    const next = (Array.isArray(raw) ? raw : []).filter((entry) => {
      if (entry && typeof entry === 'object' && entry.imageUrl) {
        const key = entry._id || entry.imageUrl;
        return key !== keyToRemove;
      }
      if (typeof entry === 'string') return entry !== keyToRemove;
      return true;
    });
    writeRawFavs(next);
    setRawItems((prev) => prev.filter((it) => (getItemKey(it)) !== keyToRemove));

    // actualizar orden persistente
    const ok = ORDER_STATE_KEY(location.pathname);
    const current = Array.isArray(savedOrderRef.current) ? savedOrderRef.current : [];
    const updated = current.filter(id => id !== keyToRemove);
    savedOrderRef.current = updated;
    try { sessionStorage.setItem(ok, JSON.stringify(updated)); } catch {}
  }, [location.pathname]);

  const canLoadMore = useMemo(() => {
    return visibleCountTarget < sortedItems.length;
  }, [visibleCountTarget, sortedItems.length]);

  const onChangeSort = (e) => setSortKey(e.target.value);
  const onChangeQuery = (e) => setQuery(e.target.value);
  const clearQuery = () => setQuery('');
  const onLoadMore = () => setPage((p) => p + 1);

  // ===== Click a detalle: guardar estado completo para restaurar luego =====
  const saveGridStateAndGo = useCallback((idToSave) => {
    const key = GRID_STATE_KEY(location.pathname, location.search);
    const payload = { y: window.scrollY || 0, page, sortKey, lastId: idToSave, query, ts: Date.now() };
    try { sessionStorage.setItem(key, JSON.stringify(payload)); } catch {}
    if (idToSave) history.push(`/detalle/${idToSave}`);
  }, [history, location.pathname, location.search, page, sortKey, query]);

  // ===== Restauración robusta del scroll cuando vuelve del detalle =====
  useEffect(() => {
    const desiredY = Math.max(0, desiredYRef.current || 0);
    if (desiredY === 0 && !pendingIdRef.current) return;

    try { if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'manual'; } catch {}

    const key = GRID_STATE_KEY(location.pathname, location.search);

    const tryScroll = () => {
      const doc = document.scrollingElement || document.documentElement;
      const canReach = (doc.scrollHeight - window.innerHeight) >= (desiredY - 4);

      if (canReach) {
        window.scrollTo(0, desiredY);
        if (Math.abs(window.scrollY - desiredY) <= 2) {
          desiredYRef.current = null;
          pendingIdRef.current = null;
          try { sessionStorage.removeItem(key); } catch {}
          try { if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'auto'; } catch {}
          return;
        }
      }

      restoreTriesRef.current += 1;

      // Fallback: centrar la tarjeta clickeada
      if (restoreTriesRef.current === 40 && pendingIdRef.current) {
        const el = document.querySelector(`[data-id="${pendingIdRef.current}"]`);
        if (el) el.scrollIntoView({ block: 'center' });
      }

      // Cortar después de un máximo
      if (restoreTriesRef.current > 80) {
        desiredYRef.current = null;
        pendingIdRef.current = null;
        try { sessionStorage.removeItem(key); } catch {}
        try { if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'auto'; } catch {}
        return;
      }

      requestAnimationFrame(tryScroll);
    };

    requestAnimationFrame(tryScroll);
  }, [rows, location.pathname, location.search]);

  return (
    <div className="fav-wrapper">
      {/* Título centrado */}
      <div className="fav-header">
        <h1 className="fav-title">Favoritos</h1>
      </div>

      {/* Controles: Buscador IZQ + Ordenar DER, con clear "×" */}
      <div className="fav-controls">
        <div className="grid-search">
          <input
            type="search"
            className="grid-search-input"
            placeholder="Buscar por título…"
            value={query}
            onChange={onChangeQuery}
            aria-label="Buscar por título"
          />
          {query && (
            <button type="button" className="grid-search-clear" onClick={clearQuery} aria-label="Limpiar búsqueda">×</button>
          )}
        </div>

        <div className="fav-spacer" />

        <div className="fav-sort">
          <label htmlFor="fav-sort-select" className="fav-sort-label">Ordenar por</label>
          <div className="fav-select-wrap">
            <select
              id="fav-sort-select"
              className="fav-sort-select"
              value={sortKey}
              onChange={onChangeSort}
            >
              <option value="random">Aleatorio</option>
              <option value="recent">Más recientes</option>
              <option value="az">A - Z</option>
              <option value="za">Z - A</option>
            </select>
            <span className="fav-select-caret" aria-hidden>▾</span>
          </div>
        </div>
      </div>

      {!user ? (
        <p className="fav-info">
          Para ver tus imágenes favoritas primero tenés que{' '}
          <Link to="/login" className="fav-login-link"><strong>iniciar sesión</strong></Link>.
        </p>
      ) : loading ? (
        <p className="fav-info">Cargando…</p>
      ) : rows.length === 0 ? (
        <p className="fav-info">No hay resultados con los filtros actuales.</p>
      ) : (
        <>
          <div className="fav-rows-wrapper">
            {rows.map((row, rIdx) => {
              const cleanRow = row.filter(Boolean);
              const variant = cleanRow.every(f => f?.orientation === ORIENT.H)
                ? 'all-h' : cleanRow.every(f => f?.orientation === ORIENT.V) ? 'all-v' : 'mixed';

              return (
                <div className={`fav-row fav-row-${cleanRow.length} ${variant}`} key={`row-${rIdx}`}>
                  {cleanRow.map((f, iIdx) => {
                    const id = f._id || `${rIdx}-${iIdx}`;
                    const cls = f.orientation === ORIENT.V ? 'tile v' : 'tile h';
                    ratioRef.current[id] = f.ratio || 1;
                    const box = imgBox[id] || { w: 0, h: 0 };
                    const isSanityId = f._id && !isUrlLike(f._id);

                    const goDetail = () => {
                      if (isSanityId) {
                        // Guardar estado completo antes de navegar
                        const keyToSave = f._id;
                        const key = GRID_STATE_KEY(location.pathname, location.search);
                        const payload = { y: window.scrollY || 0, page, sortKey, lastId: keyToSave, query, ts: Date.now() };
                        try { sessionStorage.setItem(key, JSON.stringify(payload)); } catch {}
                        history.push(`/detalle/${keyToSave}`);
                      }
                    };

                    const remove = (e) => {
                      e.preventDefault(); e.stopPropagation();
                      removeFromFavoritos(f._id || f.imageUrl);
                    };

                    return (
                      <button
                        key={id}
                        className={`fav-card ${cls}`}
                        data-id={id}
                        ref={(el) => { if (el) cardsRef.current[id] = el; }}
                        style={{ '--ratio': f.ratio || 1 }}
                        onClick={goDetail}
                        onContextMenu={(e) => e.preventDefault()}
                        aria-label={`Ver ${f.titulo || 'Foto'}`}
                      >
                        <div className="fav-img-viewport">
                          <div className="fav-img-box" style={{ '--box-w': `${box.w || 0}px`, '--box-h': `${box.h || 0}px` }}>
                            <span className="fav-toggle" onClick={remove} role="button" tabIndex={0} aria-label="Quitar de favoritos">
                              <svg className="ri-heart-fill" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" aria-hidden="true">
                                <path d="M12.001 4.529c2.349-2.109 5.979-2.04 8.242.228 2.263 2.268 2.34 5.88.236 8.236L12 21.485 3.521 12.993c-2.104-2.356-2.026-5.974.236-8.236 2.264-2.264 5.888-2.34 8.244-.228z"/>
                              </svg>
                            </span>

                            <img
                              src={f.imageUrl}
                              alt={f.titulo || 'Favorito'}
                              className="fav-img"
                              loading="lazy"
                              draggable="false"
                              onDragStart={(e) => e.preventDefault()}
                              onContextMenu={(e) => e.preventDefault()}
                            />
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              );
            })}
          </div>

          {canLoadMore && (
            <div className="fav-more-wrap">
              <button type="button" className="fav-more-btn" onClick={onLoadMore}>
                Ver más
              </button>
              <span className="fav-count">
                Mostrando {Math.min(page * PAGE_SIZE, sortedItems.length)} / {sortedItems.length}
              </span>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Favoritos;
